import { v4 } from 'uuid'
import { cn } from 'lib/utils'
import { zodValidator } from '@tanstack/zod-form-adapter'
import type { DeepKeys } from '@tanstack/react-form'
import { FieldInfo } from './field-info'
import type { ArrayFieldConfig, GenericFieldConfig, GenericFormType } from './types'
import { FieldsRenderer } from './fields-renderer'

const syncValidate = zodValidator()().validate

export const renderArrayFields = <T,>(
  fieldConfig: ArrayFieldConfig<T>,
  form: GenericFormType<T>,
  fields: GenericFieldConfig<T>,
  baseName?: string,
) => {
  const fieldArrayName = fieldConfig.name
  const baseKey = `${baseName ? `${baseName}.` : ''}${fieldArrayName.split('.').at(-1)}`
  const subFields =
    fieldConfig.subFields || fields.filter((f) => f.name.startsWith(`${fieldArrayName}[0]`))

  const isHidden = () =>
    typeof fieldConfig.visible === 'boolean'
      ? fieldConfig.visible === false
      : fieldConfig.visible?.(form.state.values, 0, baseName) === false
  return (
    <form.Field
      name={baseKey as DeepKeys<T>}
      mode="array"
      validators={{
        onChange: (value) => {
          if (fieldConfig.zodValidation instanceof Function) {
            const schema = fieldConfig.zodValidation(form.state.values)
            if (typeof schema === 'string') return schema
            return syncValidate(value, schema)
          }
          return fieldConfig.zodValidation && syncValidate(value, fieldConfig.zodValidation)
        },
      }}
    >
      {(field) => {
        if (isHidden()) return null
        return (
          <>
            {!!(field.state.value && (field.state.value as string).length) && fieldConfig.label}
            {(field.state.value as { [key: string]: string }[])?.map((_, index: number) => {
              const key = `${baseName ? `${baseName}.` : ''}${fieldArrayName.split('.').at(-1)}[${index}]`
              const className =
                typeof fieldConfig.className === 'function'
                  ? fieldConfig.className(form.state.values, index)
                  : fieldConfig.className
              return (
                <div key={key} className={cn(className, 'col-span-full', key)}>
                  <FieldsRenderer
                    fields={subFields}
                    form={form}
                    baseName={key}
                    arrayIndex={index}
                    parentField={field}
                  />
                  {fieldConfig.removeItemButton && (
                    <button
                      onClick={() => {
                        field.removeValue(index)
                      }}
                      type="button"
                      className="block"
                      aria-label="Remove"
                    >
                      {fieldConfig.removeItemButton}
                    </button>
                  )}
                </div>
              )
            })}
            {fieldConfig.addItemButton && (
              <fieldConfig.addItemButton
                state={form.state.values}
                onClick={() => {
                  const newValue =
                    typeof fieldConfig.defaultNewValue === 'function'
                      ? fieldConfig.defaultNewValue(form, field)
                      : fieldConfig.defaultNewValue
                  field.pushValue({ ...newValue, id: v4() } as never)
                }}
                type="button"
                tabIndex={0}
              />
            )}
            <FieldInfo field={field} />
          </>
        )
      }}
    </form.Field>
  )
}
